import { connect } from 'react-redux';
import _ from 'lodash';
import i18next from 'i18next';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { showErrorMessage } from '@actions/messageconfirmation';

import { getIsNotSelectable } from '@commons/utils/product';
import { ListView } from '@commons/utils/styledLibraryComponents';

import { getClientInfo } from '@selectors/client';

import EmptyState from '@backoffice/BackOfficeStoresContainer/BackOfficeStoresManagement/components/EmptyState';

import DeepsightFiltersButton from '@orders/components/FilterButton';

import { CHOICES_DROPDOWN_ACTIVE } from '@admin/utils/DropdownItems';
import {
  LastSyncContainer,
  LastSyncTitle,
  LastSyncDate,
} from '@admin/cashier/cashierProducts/styledComponents';

import { Container, FiltersContainer } from '../styledComponents';

import { cashierProductService } from '@services/cashierProduct';

import { getColumns } from './listViewColumnsConfig';

const CUSTOM_FILTERS_IDS = {
  STATUS: 'status',
  ASSOCIATED: 'associated',
  INCLUDED: 'included',
};

const QUERY_PARAMS = {
  orderBy: 'name',
  orderType: 'asc',
};

export const CHOICES_DROPDOWN_MAPPED = [
  { id: 3, name: i18next.t('GENERAL.LINKED_PLURAL'), propertyKey: 'mapped', itemValue: true },
  { id: 4, name: i18next.t('GENERAL.UNLINKED'), propertyKey: 'mapped', itemValue: false },
];
export const CHOICES_DROPDOWN_HIDDEN = [
  { id: 5, name: i18next.t('GENERAL.EXCLUDED'), propertyKey: 'hidden', itemValue: true },
  { id: 6, name: i18next.t('GENERAL.UNEXCLUDED'), propertyKey: 'hidden', itemValue: false },
];

/**
 * Used to convert the selected filter data for Status/Associated/Included into a correct payload for the API
 * Based on applyFilterSelected from src/routes/admin/products/cashierProducts/index.js
 * @param {DropdownItem[]} statusDropdownSelectedItems
 * @param {DropdownItem[]} associatedDropdownSelectedItems
 * @param {DropdownItem[]} includedDropdownSelectedItems
 * @returns an object of the shape {
 *   findActive: 'true' | 'false | 'both',
 *   findMapped: 'true' | 'false | 'both',
 *   findHidden: 'true' | 'false | 'both',
 * }
 */
const resolveCustomFilter = (
  statusDropdownSelectedItems,
  associatedDropdownSelectedItems,
  includedDropdownSelectedItems,
) => {
  const findActive =
    statusDropdownSelectedItems.length > 1
      ? 'both'
      : _.head(statusDropdownSelectedItems).filterValue.toString();

  const findMapped =
    associatedDropdownSelectedItems.length > 1
      ? 'both'
      : _.head(associatedDropdownSelectedItems).itemValue.toString();

  const findHidden =
    includedDropdownSelectedItems.length > 1
      ? 'both'
      : _.head(includedDropdownSelectedItems).itemValue.toString();

  return { findActive, findMapped, findHidden };
};

// ============================================

const SelectCashierProductsModal = (props) => {
  const {
    user,
    client: { lastSync, hasSimpleModifierManagement },
    selectCashierProductsData,
    setSelectCashierProductsData,
  } = props;

  const {
    statusDropdownSelectedItems,
    associatedDropdownSelectedItems,
    includedDropdownSelectedItems,
    selectedCashierProductIds,
    selectedCashierProducts,
  } = selectCashierProductsData;

  const userLanguageCode = _.get(user, 'lnkLanguageAccountrel.code', 'fr');

  const [isLoading, setIsLoading] = useState(false);

  // Track current selection IDs to prevent unnecessary updates
  const currentSelectionIdsRef = useRef(new Set(selectedCashierProductIds));

  // --- Filtering & Query state ---

  const customMultipleFilterStatus = {
    id: CUSTOM_FILTERS_IDS.STATUS,
    icon: '/images/icon-dropdown-grey.svg',
    list: CHOICES_DROPDOWN_ACTIVE(),
    defaultSelectedItems: statusDropdownSelectedItems,
    selectedItems: statusDropdownSelectedItems,
    setSelectedItems: () => {}, // items selection is done by useEffect on [filters]
    allMessage: i18next.t('GENERAL.ACTIVES_INACTIVES'),
  };
  const customMultipleFilterAssociated = {
    id: CUSTOM_FILTERS_IDS.ASSOCIATED,
    icon: '/images/inpulse/link-black-small.svg',
    list: CHOICES_DROPDOWN_MAPPED,
    defaultSelectedItems: associatedDropdownSelectedItems,
    selectedItems: associatedDropdownSelectedItems,
    setSelectedItems: () => {}, // items selection is done by useEffect on [filters]
    allMessage: i18next.t('GENERAL.LINKED_OR_NOT'),
  };
  const customMultipleFilterIncluded = {
    id: CUSTOM_FILTERS_IDS.INCLUDED,
    icon: '/images/inpulse/eye-black-small.svg',
    list: CHOICES_DROPDOWN_HIDDEN,
    defaultSelectedItems: includedDropdownSelectedItems,
    selectedItems: includedDropdownSelectedItems,
    setSelectedItems: () => {}, // items selection is done by useEffect on [filters]
    allMessage: i18next.t('GENERAL.EXCLUDED_OR_NOT'),
  };

  const customMultipleDropDowns = [
    customMultipleFilterStatus,
    customMultipleFilterAssociated,
    customMultipleFilterIncluded,
  ];

  const [filters, setFilters] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [applyFilters, setApplyFilters] = useState(true);

  // The list of CashierProducts returned by the API
  const [queriedCashierProducts, setQueriedCashierProducts] = useState([]);
  // The same list, with its element to properly show selected items
  const [cashierProductsList, setCashierProductsList] = useState([]);

  // Wrapper to only notify parent when selection actually changes
  const handleSelectionChange = useCallback(
    (newSelection) => {
      const currentIds = currentSelectionIdsRef.current;
      const newSelectionIds = new Set(newSelection.map(({ id }) => id));

      // Check if selection actually changed
      const hasChanged =
        newSelectionIds.size !== currentIds.size ||
        [...newSelectionIds].some((id) => !currentIds.has(id));

      if (hasChanged) {
        currentSelectionIdsRef.current = newSelectionIds;
        setSelectCashierProductsData({
          ...selectCashierProductsData,
          selectedCashierProductIds: newSelection.map(({ id }) => id),
          selectedCashierProducts: newSelection,
        });
      }
    },
    [selectCashierProductsData, setSelectCashierProductsData],
  );

  // ------------------

  // Fetch of all CashierProducts matching the chosen filters
  useEffect(() => {
    if (!applyFilters) {
      return;
    }

    setIsLoading(true);

    (async function loadData() {
      try {
        const clientId = _.get(user, 'clientId');

        const cashierProducts = await cashierProductService.getPaginatedCashierProductsByClientId(
          clientId,
          resolveCustomFilter(
            statusDropdownSelectedItems,
            associatedDropdownSelectedItems,
            includedDropdownSelectedItems,
          ),
          QUERY_PARAMS,
          true,
        );

        const formattedCashierProducts = cashierProducts.cashierProducts.map((cashierProduct) => ({
          ...cashierProduct,
          isRowSelected: false,
          mapped: cashierProduct.product_cashier_product_mapping_id, // for proper column display
        }));

        setQueriedCashierProducts(formattedCashierProducts);
      } catch (error) {
        showErrorMessage(i18next.t('ADMIN.CASHIER_PRODUCTS.LOAD_ERROR'));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [applyFilters]);

  /**
   * After a query for CashierProducts is made, set the isRowSelected based on the step data
   * so that selection is kept through navigation.
   */
  useEffect(() => {
    const formattedCashierProducts = queriedCashierProducts.map((cashierProduct) => ({
      ...cashierProduct,
      isRowSelected: !!selectedCashierProductIds.find(
        (selectedId) => selectedId === cashierProduct.id,
      ),
    }));

    setCashierProductsList(formattedCashierProducts);
  }, [queriedCashierProducts]);

  // Update isRowSelected and isNotSelectable based on current selection
  useEffect(() => {
    if (!hasSimpleModifierManagement || !cashierProductsList.length) {
      return;
    }

    // Update ref with current selection
    currentSelectionIdsRef.current = new Set(selectedCashierProductIds);

    setCashierProductsList((prev) =>
      prev.map((cashierProduct) => ({
        ...cashierProduct,
        isRowSelected: selectedCashierProductIds.includes(cashierProduct.id),
        isNotSelectable: getIsNotSelectable(cashierProduct, selectedCashierProducts || []),
      })),
    );
  }, [selectedCashierProducts, hasSimpleModifierManagement]);

  // Storing the filters selection in the step data
  useEffect(() => {
    if (!filters) {
      return;
    }
    setSelectCashierProductsData({
      ...selectCashierProductsData,
      statusDropdownSelectedItems: filters[CUSTOM_FILTERS_IDS.STATUS].selectedItems,
      associatedDropdownSelectedItems: filters[CUSTOM_FILTERS_IDS.ASSOCIATED].selectedItems,
      includedDropdownSelectedItems: filters[CUSTOM_FILTERS_IDS.INCLUDED].selectedItems,
    });
  }, [filters]);

  const [lastSyncDate, setLastSyncDate] = useState({});

  useEffect(() => {
    const formattedDate = moment(lastSync);

    setLastSyncDate({ date: formattedDate.format('DD/MM'), hour: formattedDate.format('HH:mm') });
  }, []);

  return (
    <Container>
      <ListView
        columns={getColumns({ hasSimpleModifierManagement })}
        data={cashierProductsList}
        disableFullSelection={hasSimpleModifierManagement}
        isLoading={isLoading}
        languageCode={userLanguageCode}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <FiltersContainer>
            <DeepsightFiltersButton
              advancedFilters={advancedFilters}
              applyFilters={applyFilters}
              customMultipleDropDowns={customMultipleDropDowns}
              filters={filters}
              isLoading={isLoading}
              setAdvancedFilters={setAdvancedFilters}
              setApplyFilters={setApplyFilters}
              setFilters={setFilters}
              textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
            />
            {!_.isEmpty(lastSyncDate) && (
              <LastSyncContainer>
                <LastSyncTitle>{i18next.t('GENERAL.LAST_SYNC')}</LastSyncTitle>
                <LastSyncDate>
                  {i18next.t('ADMIN.CASHIER_PRODUCTS.LAST_SYNC_DATE', {
                    date: lastSyncDate.date,
                    hour: lastSyncDate.hour,
                  })}
                </LastSyncDate>
              </LastSyncContainer>
            )}
          </FiltersContainer>
        )}
        setSelectedItems={
          hasSimpleModifierManagement
            ? handleSelectionChange
            : (selectedItems) => {
                setSelectCashierProductsData({
                  ...selectCashierProductsData,
                  selectedCashierProductIds: selectedItems.map(({ id }) => id),
                });
              }
        }
        disableFooter
        forceEnableSelection
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SelectCashierProductsModal);
